import React from 'react';
import { _cs, isNotDefined } from '@togglecorp/fujs';
import Portal from '../Portal.js';
import usePositionTracking from '../../hooks/usePositionTracking.js';
import useMousePositionTracking from '../../hooks/useMousePositionTracking.js';
import useUnmountTransition from '../../hooks/useUnmountTransition.js';
import { genericMemo } from '../../utils/index.js';
import styles from './styles.css.js';

const defaultPlacement = {
  top: 'unset',
  right: 'unset',
  bottom: 'unset',
  left: 'unset'
};

function getFloatPlacementOnParentRect(bcr) {
  let placementDirection = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'vertical';
  const placement = { ...defaultPlacement
  };
  let horizontalPosition;
  let verticalPosition;
  let contentWidth = 'auto';
  let maxHeight = 'auto';
  const offsetX = 0;

  if (bcr) {
    const {
      x,
      y,
      width,
      height
    } = bcr;
    const cX = window.innerWidth / 2;
    const cY = window.innerHeight / 2;

    if (placementDirection === 'horizontal') {
      const OFFSET = 10;
      const xEnd = bcr.x + bcr.width;
      const startDiff = bcr.x;
      const endDiff = window.innerWidth - xEnd;

      if (startDiff > endDiff) {
        horizontalPosition = 'left';
      } else {
        horizontalPosition = 'right';
      }

      verticalPosition = cY - bcr.y > 0 ? 'bottom' : 'top';

      if (horizontalPosition === 'left') {
        placement.right = `${window.innerWidth - x + OFFSET}px`;
      } else if (horizontalPosition === 'right') {
        placement.left = `${x + width + OFFSET}px`;
      }

      if (verticalPosition === 'top') {
        placement.bottom = `${window.innerHeight - y - OFFSET * 2}px`;
      } else if (verticalPosition === 'bottom') {
        placement.top = `${y}px`;
      }
    } else {
      horizontalPosition = cX - bcr.x > 0 ? 'right' : 'left';
      verticalPosition = cY - bcr.y > 0 ? 'bottom' : 'top';

      if (horizontalPosition === 'left') {
        placement.right = `${Math.min(window.innerWidth, window.innerWidth - x - width - offsetX)}px`;
      } else if (horizontalPosition === 'right') {
        placement.left = `${Math.max(0, x + offsetX)}px`;
      }

      if (verticalPosition === 'top') {
        placement.bottom = `${window.innerHeight - y + 20}px`;
      } else if (verticalPosition === 'bottom') {
        placement.top = `${y + height + 20}px`;
      }
    }

    contentWidth = `${width}px`;
    maxHeight = `calc(50vh - ${height / 2}px)`;
  }

  return {
    placement,
    width: contentWidth,
    horizontalPosition,
    verticalPosition,
    maxHeight
  };
}

function getFloatPlacementOnMousePosition(x, y) {
  const placement = { ...defaultPlacement
  };

  if (isNotDefined(x) || isNotDefined(y)) {
    return {
      placement,
      horizontalPosition: 'left',
      verticalPosition: 'bottom',
      width: 'auto',
      maxHeight: 'auto'
    };
  }

  const offsetX = -20;
  const offsetY = 6;
  const cX = window.innerWidth / 2;
  const cY = window.innerHeight / 2;
  const horizontalPosition = cX - x > 0 ? 'right' : 'left';
  const verticalPosition = cY - y > 0 ? 'bottom' : 'top';

  if (horizontalPosition === 'left') {
    placement.right = `${window.innerWidth - x - offsetX}px`;
  } else if (horizontalPosition === 'right') {
    placement.left = `${x + offsetX}px`;
  }

  if (verticalPosition === 'top') {
    placement.bottom = `${window.innerHeight - y + offsetY}px`;
  } else if (verticalPosition === 'bottom') {
    placement.top = `${y + offsetY}px`;
  }

  return {
    placement,
    horizontalPosition,
    verticalPosition,
    width: 'auto',
    maxHeight: 'auto'
  };
}

function usePopupFeatures(props) {
  const {
    show,
    elementRef,
    contentRef,
    className,
    contentClassName,
    children,
    tipClassName,
    matchParentWidth,
    useMousePosition,
    parentRef,
    popupPlacementDirection
  } = props;
  const [delayedShow, setDelayedShow] = React.useState();
  const dummyRef = React.useRef(null);
  const parentBCR = usePositionTracking(parentRef ?? dummyRef, delayedShow && !useMousePosition, !parentRef);
  const mousePositions = useMousePositionTracking((useMousePosition && delayedShow) ?? false);
  const mouseX = mousePositions === null || mousePositions === void 0 ? void 0 : mousePositions[0];
  const mouseY = mousePositions === null || mousePositions === void 0 ? void 0 : mousePositions[1];
  const {
    placement,
    width,
    horizontalPosition,
    verticalPosition,
    maxHeight
  } = useMousePosition ? getFloatPlacementOnMousePosition(mouseX, mouseY) : getFloatPlacementOnParentRect(parentBCR, popupPlacementDirection);
  React.useEffect(() => {
    setDelayedShow(show);
  }, [show]);
  const popupChildren = /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement("div", {
    style: { ...placement,
      width: matchParentWidth ? width : undefined,
      maxHeight
    },
    ref: elementRef,
    className: _cs(styles.popup, className, horizontalPosition === 'left' ? styles.left : styles.right, verticalPosition === 'top' ? styles.top : styles.bottom, !show && styles.hidden, popupPlacementDirection === 'horizontal' && styles.horizontalPlacement)
  }, /*#__PURE__*/React.createElement("div", {
    className: _cs(styles.tip, tipClassName)
  }), /*#__PURE__*/React.createElement("div", {
    ref: contentRef,
    className: _cs(styles.content, contentClassName)
  }, children)));
  return {
    dummyRef,
    popupChildren
  };
}

function Popup(props) {
  const {
    children,
    className,
    contentClassName,
    contentRef,
    elementRef,
    show,
    tipClassName,
    freeWidth = false,
    onUnmount,
    parentRef,
    placementDirection
  } = props;
  const {
    dummyRef,
    popupChildren
  } = usePopupFeatures({
    children,
    className,
    contentClassName,
    contentRef,
    elementRef,
    show,
    tipClassName,
    matchParentWidth: !freeWidth || placementDirection === 'horizontal',
    parentRef,
    popupPlacementDirection: placementDirection
  });
  const unmountRef = React.useRef();
  const shouldUnmount = useUnmountTransition(show);
  React.useEffect(() => {
    if (onUnmount && unmountRef.current === false && shouldUnmount === true) {
      onUnmount();
    }

    unmountRef.current = shouldUnmount;
  }, [shouldUnmount, onUnmount]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, !parentRef && /*#__PURE__*/React.createElement("div", {
    ref: dummyRef,
    className: styles.dummy
  }), !shouldUnmount && popupChildren);
}

var Popup$1 = genericMemo(Popup);

export { Popup$1 as default, usePopupFeatures };
//# sourceMappingURL=index.js.map
